// #2542 — import getFleetHttpUrl() (lazy, post-hydration) instead of the
// module-eval-time getFleetHttpUrl() constant, which resolves before ConfigProvider
// has written window.__2HIVES_CONFIG__ and always falls back to the wrong host.
import { getFleetHttpUrl } from "../lib/fleetRoles";
import type { FleetJob } from "./client";
export type { FleetJob } from "./client";

export interface FleetProject {
  name:        string;
  status:      "cloning" | "active" | string;
  github_url?: string;
  box_id?:     string;
  role?:       string;
  created_at?: string;
  // #2147 — webhook fan-out sync state (mirrors UserProject.last_synced_*)
  last_synced_at?:  string;
  last_synced_sha?: string;
  last_synced_by?:  string;
  last_synced_ref?: string;
  sync_status?:     "" | "pending_pull" | "synced" | "diverged" | string;
  // #3052 — mirrors UserProject.caller_role so AdminProjects.tsx can pass
  // the chip kind through to HiveRow as the /admin/hives-side fallback.
  caller_role?:     "owner" | "member" | "admin" | "invited";
}

// Fired only when /admin/user/me returns 401 (session expired).
// All other 401s are resource-level auth failures and must NOT trigger logout.
let _onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(fn: () => void) {
  _onUnauthorized = fn;
}
export function callUnauthorizedHandler() {
  _onUnauthorized?.();
}

function getToken(): string | null {
  return localStorage.getItem("jwt");
}

function authHdr(token: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fleetGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const url = `${getFleetHttpUrl()}${path}${qs}`;
  const res = await fetch(url, { headers: authHdr(getToken()) });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Fleet API ${res.status}: ${path}`);
  }
  return res.json();
}

async function fleetPost<T>(path: string, body: unknown, token?: string | null): Promise<T> {
  const t = token ?? getToken();
  const res = await fetch(`${getFleetHttpUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHdr(t) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Fleet API ${res.status}: ${path}`);
  }
  return res.json();
}

async function fleetPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getFleetHttpUrl()}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHdr(getToken()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Fleet API ${res.status}: ${path}`);
  }
  return res.json();
}

export const fleetApi = {
  listJobs: async (params?: { status?: string; target?: string; project?: string; limit?: number }): Promise<FleetJob[]> => {
    const qp: Record<string, string> = {};
    if (params?.status)  qp.status  = params.status;
    if (params?.target)  qp.target  = params.target;
    if (params?.project) qp.project = params.project;
    if (params?.limit)   qp.limit   = String(params.limit);
    const res = await fleetGet<{ jobs: FleetJob[]; count: number }>(
      "/fleet/jobs/list",
      Object.keys(qp).length ? qp : undefined,
    );
    return res.jobs;
  },

  getJobThread: async (id: number): Promise<FleetJob[]> => {
    const res = await fleetGet<{ thread: FleetJob[]; count: number }>(`/fleet/jobs/${id}/thread`);
    return res.thread;
  },

  // ── Job create (backend #482) — single POST replaces 5-step /db/query dance ─

  createJob: async (
    body: {
      target:          string;
      title:           string;
      detail?:         string;
      status?:         "queued" | "waiting";
      follow_up_at?:   string | null;
      follow_up_note?: string | null;
      send?:           boolean;
      parent_id?:      number;
      project?:        string;
    },
    token: string,
  ): Promise<{ job_id: number; job: FleetJob; delivery: "ws" | "queued" | "none" }> => {
    return fleetPost("/fleet/jobs", { source: "admin", ...body }, token);
  },

  updateJob: async (id: number, patch: { status?: string; note?: string; blocker?: string | null; decision_options?: Array<{ label: string; detail?: string }> | null; follow_up_at?: string | null; follow_up_note?: string | null }): Promise<{ job: FleetJob }> => fleetPatch(`/fleet/jobs/${id}`, patch),

  founderTodoSsmPut: async (path: string, value: string, type: "SecureString" | "String" = "SecureString"): Promise<{ ok: true; path: string; version: number }> => fleetPost("/admin/founder-todo/ssm-put", { path, value, type }),

  // ── Job history (backend #520) — audit-log timeline ──────────────────────────

  getJobHistory: async (id: number): Promise<JobHistoryEvent[]> => {
    try {
      const res = await fleetGet<{ history: JobHistoryEvent[]; count: number }>(
        `/fleet/jobs/${id}/history`,
      );
      return res.history;
    } catch {
      return [];
    }
  },

  // ── Card token-usage time-series (backend #2007) ─────────────────────────────
  getCardTokenUsage: async (id: number): Promise<CardTokenUsageRow[]> => {
    try {
      const res = await fleetGet<{ card_id: number; count: number; rows: CardTokenUsageRow[] }>(
        `/fleet/jobs/${id}/token-usage`,
      );
      return res.rows ?? [];
    } catch {
      return [];
    }
  },

  // ── Cache stats summary (#2262) — hit rate + savings over a window ───────────
  // #2964: `/fleet/token-usage` is a POST route on TwoHivesAdminFleet; the
  // SPA previously sent GET which the API GW rejected with 405. Switched
  // verb to POST and moved range/group into the JSON body to match the
  // backend handler contract.
  getCacheStats: async (range: "1h" | "24h" | "7d" | "30d" = "7d"): Promise<CacheStatsSummary | null> => {
    try {
      const res = await fleetPost<{ cache_summary?: CacheStatsSummary }>(
        `/fleet/token-usage`,
        { range, group: "session" },
      );
      return res.cache_summary ?? null;
    } catch {
      return null;
    }
  },

  getProjects: async (): Promise<FleetProject[]> => {
    try {
      const res = await fleetGet<unknown>("/fleet/projects");
      // Backend returns {"projects":[...]}; handle both that shape and a bare array.
      const raw: unknown[] = Array.isArray(res)
        ? res
        : Array.isArray((res as { projects?: unknown })?.projects)
          ? (res as { projects: unknown[] }).projects
          : [];
      // Normalise: accept both object shape (new) and legacy string shape.
      return raw.map((p) =>
        typeof p === "string" ? { name: p, status: "active" as const } : (p as FleetProject)
      );
    }
    catch { return []; }
  },

  getProjectNames: async (): Promise<string[]> =>
    fleetApi.getProjects().then((ps) => ps.map((p) => p.name)),

  // Dispatch to ceo to add a new session to TMUX-SESSIONS and start it.
  sessionAdd: async (session: string, dir: string, box: string, model: string, tier: string, project: string): Promise<void> => {
    await fleetPost<unknown>("/fleet/jobs", {
      source: "admin", target: "ceo",
      title:  `add_session: ${session}`,
      detail: `Add new Claude session:\nProject: ${project}\nDir: ${dir}\nSession: ${session}\nBox: ${box}\nModel: ${model}\nTier: ${tier}\n\nAdd row to TMUX-SESSIONS in CLAUDE.md then run: ./tmux_up ${session}`,
      project, status: "queued", send: true,
    });
  },

  // Dispatch to ceo to remove a session from TMUX-SESSIONS (after killing it).
  sessionRemove: async (session: string, project: string): Promise<void> => {
    await fleetPost<unknown>("/fleet/jobs", {
      source: "admin", target: "ceo",
      title:  `remove_session: ${session}`,
      detail: `Remove session ${session} from project ${project}.\nKill tmux session if running and remove row from TMUX-SESSIONS in CLAUDE.md.`,
      project, status: "queued", send: true,
    });
  },

  // Stop a session — dispatches ./tmux_down to ceo via fleet job.
  sessionStop: async (session: string, project: string): Promise<void> => {
    await fleetPost<unknown>("/fleet/jobs", {
      source: "admin", target: "ceo",
      title:  `stop_session: ${session}`,
      detail: `./tmux_down ${session}`,
      project, status: "queued", send: true,
    });
  },

  // Dispatch a fleet job to ceo to bring a session up via tmux_up.
  sessionStart: async (session: string, project: string): Promise<void> => {
    await fleetPost<unknown>("/fleet/jobs", {
      source: "admin", target: "ceo",
      title:  `start_session: ${session}`,
      detail: `./tmux_up ${session}`,
      project, status: "queued", send: true,
    });
  },

  // ── Tables API (backend #621) ──────────────────────────────────────────────

  listTables: async (params?: { project?: string }): Promise<FleetTableDef[]> => {
    const qp: Record<string, string> = {};
    if (params?.project) qp.project = params.project;
    const res = await fleetGet<{ tables: FleetTableDef[] }>(
      "/fleet/tables",
      Object.keys(qp).length ? qp : undefined,
    );
    // Backend may omit icon — apply a fallback map so the rail always shows an emoji
    return res.tables.map((t) => ({
      ...t,
      icon: t.icon ?? TABLE_ICON_MAP[t.key] ?? TABLE_ICON_MAP[t.key.split(":")[0]] ?? "📊",
    }));
  },

  getTableRows: async (
    key: string,
    params?: { project?: string; limit?: number; offset?: number; sort?: string; filter?: string },
  ): Promise<FleetTableResult> => {
    const qp: Record<string, string> = {};
    if (params?.project) qp.project = params.project;
    if (params?.limit)   qp.limit   = String(params.limit);
    if (params?.offset)  qp.offset  = String(params.offset);
    if (params?.sort)    qp.sort    = params.sort;
    if (params?.filter)  qp.filter  = params.filter;
    // Do NOT catch here — let errors propagate so the UI shows a real error instead of mock data
    const raw = await fleetGet<FleetTableResult>(
      `/fleet/tables/${encodeURIComponent(key)}/rows`,
      Object.keys(qp).length ? qp : undefined,
    );
    // Backend sends {name, type, sortable, filterable}; normalise to {key, label, type, sortable}
    return {
      ...raw,
      columns: (raw.columns as BackendColumn[]).map((c) => ({
        key:      c.key      ?? c.name      ?? "",
        label:    c.label    ?? c.name      ?? "",
        type:     (c.type    ?? "string") as FleetTableColumn["type"],
        sortable: c.sortable ?? false,
      })),
    };
  },

  tableRowsCsvUrl: (key: string, params?: { project?: string; sort?: string; filter?: string }): string => {
    const qp: Record<string, string> = {};
    if (params?.project) qp.project = params.project;
    if (params?.sort)    qp.sort    = params.sort;
    if (params?.filter)  qp.filter  = params.filter;
    const qs = Object.keys(qp).length ? "?" + new URLSearchParams(qp).toString() : "";
    const token = localStorage.getItem("jwt") ?? "";
    return `${getFleetHttpUrl()}/fleet/tables/${encodeURIComponent(key)}/rows.csv${qs}&token=${token}`;
  },
};

// ── Job history type ──────────────────────────────────────────────────────────

export interface JobHistoryEvent {
  id:          number;
  job_id:      number;
  changed_at:  string;
  actor:       string;
  event:       string;
  old_status:  string | null;
  new_status:  string | null;
  detail:      string | null;
}

// ── Card token-usage (backend #2007) — one row per assistant turn ────────────
export interface CardTokenUsageRow {
  id:              number;
  session:         string;
  project:         string;
  ts:              string;
  turn_index:      number | null;
  model:           string;
  input_tokens:    number;
  output_tokens:   number;
  cache_read:      number;
  cache_write:     number;
  cost_usd_cents:  number;
  latency_ms:      number | null;
}

// ── Cache stats summary (#2262) ───────────────────────────────────────────────
export interface CacheStatsSummary {
  total_input_tokens:       number;
  total_cache_read_tokens:  number;
  total_cache_write_tokens: number;
  total_output_tokens:      number;
  cache_hit_rate_pct:       number;
  alert:                    boolean;  // true when hit rate < 50%
}

// ── Tables types ──────────────────────────────────────────────────────────────

interface BackendColumn {
  key?:       string;
  name?:      string;
  label?:     string;
  type?:      string;
  sortable?:  boolean;
  filterable?: boolean;
}

export interface FleetTableDef {
  key:         string;
  label:       string;
  icon?:       string;
  category:    string;
  row_count:   number;
  description?: string;
}

export interface FleetTableResult {
  columns: FleetTableColumn[];
  rows:    Record<string, unknown>[];
  total:   number;
  limit:   number;
  offset:  number;
}

export interface FleetTableColumn {
  key:      string;
  label:    string;
  type:     "string" | "number" | "date" | "json";
  sortable: boolean;
}

// ── Icon fallback map (backend does not send icons) ───────────────────────────

const TABLE_ICON_MAP: Record<string, string> = {
  entities:       "👤",
  fleet_jobs:     "📋",
  ap_invoices:    "💳",
  ledger_entries: "📒",
  accounts:       "💼",
  research_papers:"🔬",
  bom_entries:    "🔩",
  registrations:  "📍",
  fundraising:    "💰",
};
